import { useEffect, useRef, useState } from 'react';

/**
 * Hook that triggers a reveal animation when an element scrolls into view.
 * Returns a ref to attach to the element and a boolean indicating visibility.
 */
export function useScrollReveal({ threshold = 0.15, delay = 0, once = true } = {}) {
    const ref = useRef(null);
    const [isVisible, setIsVisible] = useState(false);

    useEffect(() => {
        const element = ref.current;
        if (!element) return;

        // Respect prefers-reduced-motion: reveal immediately and skip the
        // observer/timers entirely so there is no delayed reveal or motion.
        const reducedMotion = typeof window !== 'undefined'
            && window.matchMedia
            && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        if (reducedMotion) {
            setIsVisible(true);
            return;
        }

        const observer = new IntersectionObserver(
            ([entry]) => {
                if (entry.isIntersecting) {
                    if (delay > 0) {
                        setTimeout(() => setIsVisible(true), delay);
                    } else {
                        setIsVisible(true);
                    }
                    if (once) observer.unobserve(element);
                } else if (!once) {
                    setIsVisible(false);
                }
            },
            { threshold }
        );

        observer.observe(element);
        return () => observer.disconnect();
    }, [threshold, delay, once]);

    return [ref, isVisible];
}
